import { appAlert, appConfirm, appPrompt } from "./appDialogs";
import { API_ORIGIN, apiUrl } from "./apiOrigin";
// =============================================================================
// DashboardPanels.js  (v2.0)
// v2.0: REDESIGNED manager screen (2026-10). An explanation at the top of what
//       a panel is (the answer to one question, shown as a grid, graph or
//       single value) and what a dashboard is (a set of panels that can run
//       in its own browser window and updates in real time). Below it, two
//       columns:
//         - Panels (left): every panel with what it shows, how it displays,
//           its refresh rate and which dashboards use it; + New / Edit open
//           the panel builder, where a description is interpreted and the
//           panel TESTED (live preview) before it is saved; Delete.
//         - Dashboards (right): each dashboard's name and component panels;
//           panels added / removed in place; View (popup, panels can be
//           moved and resized and the layout saved), Open in a new window
//           (the ?wall= view), + New dashboard, Delete.
//       The live canvas (polling, resize, drag-to-reorder) is one component,
//       DashboardCanvas, shared by the popup and the wall view. Backend
//       unchanged (headoffice.js /dash/* routes).
// v1.3 and earlier: single canvas with a Panels dropdown, Manage panels
//       popup and a dashboards bar (see version history in git).
// =============================================================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
const API = apiUrl("");
const token = () => localStorage.getItem("token") || localStorage.getItem("authToken") || "";
async function post(path, body) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token()}`
    },
    body: JSON.stringify(body || {})
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
  return d;
}
const S = {
  btn: (c, solid) => ({
    background: solid ? c : "#fff",
    color: solid ? "#fff" : c,
    border: `1px solid ${c}`,
    borderRadius: 6,
    padding: "5px 11px",
    fontSize: 12.5,
    fontWeight: 700,
    cursor: "pointer"
  }),
  inp: {
    border: "1px solid #D9D9D9",
    borderRadius: 6,
    padding: "6px 9px",
    fontSize: 13
  },
  lab: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.4,
    color: "#5D5D5D",
    textTransform: "uppercase"
  }
};
const fmtT = iso => iso ? new Date(iso).toLocaleTimeString("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit"
}) : "—";
const fmtDT = iso => iso ? new Date(iso).toLocaleString("en-GB", {
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit"
}) : "—";

/* ---------------- visual renderers (no chart library) ---------------- */
function BarViz({
  rows
}) {
  const max = Math.max(1, ...rows.map(r => r.value));
  return <div>
      {rows.slice(0, 14).map(r => <div key={r.label} style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      marginBottom: 4
    }}>
          <div style={{
        width: 130,
        fontSize: 11.5,
        color: "#434343",
        textAlign: "right",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }} title={r.label}>{r.label}</div>
          <div style={{
        flex: 1,
        background: "#F5F5F5",
        borderRadius: 4,
        height: 18,
        position: "relative"
      }}>
            <div style={{
          width: `${r.value / max * 100}%`,
          background: "#0E8CFF",
          height: "100%",
          borderRadius: 4
        }} />
            <span style={{
          position: "absolute",
          right: 6,
          top: 1,
          fontSize: 11,
          fontWeight: 800,
          color: "#111"
        }}>{r.value}</span>
          </div>
        </div>)}
      {rows.length === 0 && <div style={{
      color: "#8C8C8C",
      fontSize: 12.5
    }}>No data in this window.</div>}
    </div>;
}
function LineViz({
  rows
}) {
  if (!rows.length) return <div style={{
    color: "#8C8C8C",
    fontSize: 12.5
  }}>No data in this window.</div>;
  const W = 420,
    H = 120,
    max = Math.max(1, ...rows.map(r => r.value));
  const pts = rows.map((r, i) => `${i / Math.max(1, rows.length - 1) * (W - 20) + 10},${H - 12 - r.value / max * (H - 30)}`).join(" ");
  return <svg viewBox={`0 0 ${W} ${H}`} style={{
    width: "100%",
    height: "auto"
  }}>
      <polyline points={pts} fill="none" stroke="#0E8CFF" strokeWidth="2.5" />
      {rows.map((r, i) => <circle key={i} cx={i / Math.max(1, rows.length - 1) * (W - 20) + 10} cy={H - 12 - r.value / max * (H - 30)} r="3" fill="#0E8CFF">
          <title>{r.label}: {r.value}</title>
        </circle>)}
      <text x={10} y={H - 2} fontSize="9" fill="#5D5D5D">{rows[0].label}</text>
      <text x={W - 10} y={H - 2} fontSize="9" fill="#5D5D5D" textAnchor="end">{rows[rows.length - 1].label}</text>
    </svg>;
}
function TableViz({
  rows
}) {
  return <table style={{
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12.5
  }}>
      <tbody>
        {rows.slice(0, 20).map(r => <tr key={r.label} style={{
        borderBottom: "1px solid #F5F5F5"
      }}>
            <td style={{
          padding: "4px 6px"
        }}>{r.label}</td>
            <td style={{
          padding: "4px 6px",
          textAlign: "right",
          fontWeight: 800
        }}>{r.value}</td>
          </tr>)}
        {rows.length === 0 && <tr><td style={{
          color: "#8C8C8C",
          padding: 6
        }}>No data in this window.</td></tr>}
      </tbody>
    </table>;
}
function FeedViz({
  items
}) {
  return <div style={{
    maxHeight: 260,
    overflowY: "auto"
  }}>
      {items.map((it, i) => <div key={i} style={{
      borderBottom: "1px solid #F5F5F5",
      padding: "5px 2px",
      fontSize: 12.5
    }}>
          <span style={{
        fontWeight: 800,
        color: "#0E8CFF"
      }}>{it.pharmacy}</span>
          <span style={{
        color: "#5D5D5D",
        marginLeft: 6,
        fontSize: 11.5
      }}>{fmtDT(it.when)}</span>
          <div>{it.text}</div>
        </div>)}
      {items.length === 0 && <div style={{
      color: "#8C8C8C",
      fontSize: 12.5
    }}>Nothing yet in this window.</div>}
    </div>;
}
function PanelBody({
  result
}) {
  if (!result) return <div style={{
    color: "#8C8C8C",
    fontSize: 12.5
  }}>Loading…</div>;
  if (result.error) return <div style={{
    color: "#CF1322",
    fontSize: 12.5
  }}>{result.error}</div>;
  if (result.kind === "feed") return <FeedViz items={result.items || []} />;
  const rows = result.rows || [];
  const visual = result.visual;
  if (visual === "counter") {
    return <div style={{
      fontSize: 44,
      fontWeight: 900,
      color: "#0E8CFF",
      textAlign: "center",
      padding: "14px 0"
    }}>{result.total ?? 0}</div>;
  }
  if (visual === "line") return <LineViz rows={rows} />;
  if (visual === "table") return <TableViz rows={rows} />;
  return <BarViz rows={rows} />;
}
const VISUAL_LABEL = {
  bar: "Bar chart",
  line: "Line graph",
  table: "Grid",
  counter: "Single value",
  feed: "Live feed"
};
const visualOf = p => VISUAL_LABEL[p?.spec?.kind === "feed" ? "feed" : p?.spec?.visual] || "Bar chart";
const POLL_CHOICES = [10, 30, 60, 120, 300, 900];
const pollLabel = s => s < 60 ? `${s}s` : `${s / 60} min`;
const card = {
  background: "#fff",
  border: "1px solid #F0F0F0",
  borderRadius: 10,
  padding: "12px 14px"
};

/* ---------------- panel builder dialog: describe, interpret, TEST, save ---------------- */
function PanelEditor({
  initial,
  onClose,
  onSaved
}) {
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [pollSec, setPollSec] = useState(initial?.pollSec || 60);
  const [spec, setSpec] = useState(initial?.spec || null);
  const [readback, setReadback] = useState(initial?.readback || "");
  const [preview, setPreview] = useState(null); // result of a test run
  const [busy, setBusy] = useState(""); // "" | "interpret" | "test" | "save"
  const [err, setErr] = useState(null);
  const test = async s => {
    const sp = s || spec;
    if (!sp) return;
    setBusy("test");
    setErr(null);
    setPreview(null);
    try {
      const d = await post("/api/headoffice/dash/run", {
        spec: sp
      });
      setPreview({
        ...d,
        visual: sp.visual
      });
    } catch (e) {
      setPreview({
        error: e.message
      });
    } finally {
      setBusy("");
    }
  };
  const interpret = async () => {
    setBusy("interpret");
    setErr(null);
    setPreview(null);
    try {
      const d = await post("/api/headoffice/dash/spec", {
        description
      });
      setSpec(d.spec);
      setReadback(d.readback);
      await test(d.spec); // test straight away, so the preview shows what will be saved
    } catch (e) {
      setErr(e.message);
      setBusy("");
    }
  };
  const save = async () => {
    setBusy("save");
    setErr(null);
    try {
      let s = spec,
        rb = readback;
      if (!s) {
        const d = await post("/api/headoffice/dash/spec", {
          description
        });
        s = d.spec;
        rb = d.readback;
      }
      const r = await post("/api/headoffice/dash/panels/save", {
        id: initial?._id,
        name,
        description,
        pollSec: Number(pollSec) || 60,
        spec: s,
        readback: rb
      });
      onSaved(r.id);
    } catch (e) {
      setErr(e.message);
      setBusy("");
    }
  };
  const working = !!busy;
  return <div role="dialog" aria-modal="true" style={{
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9600,
    padding: 16
  }}>
      <div style={{
      background: "#fff",
      borderRadius: 8,
      width: "min(720px, 96vw)",
      maxHeight: "92vh",
      overflowY: "auto",
      padding: 18
    }}>
        <div style={{
        fontWeight: 800,
        fontSize: 16,
        marginBottom: 4
      }}>{initial?._id ? "Edit panel" : "New panel"}</div>
        <div style={{
        fontSize: 12.5,
        color: "#5D5D5D",
        marginBottom: 12,
        lineHeight: 1.5
      }}>
          Describe the question this panel answers in plain English. <b>Interpret &amp; test</b> shows how it was understood
          and runs it against your branches now, so you can check the answer before saving.</div>
        {err && <div role="alert" style={{
        color: "#CF1322",
        fontSize: 12.5,
        marginBottom: 8
      }}>{err}</div>}
        <label style={S.lab} htmlFor="pe-name">Panel name</label>
        <input id="pe-name" style={{
        ...S.inp,
        width: "100%",
        boxSizing: "border-box",
        margin: "2px 0 10px"
      }} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Flu clinic bookings — live" />
        <label style={S.lab} htmlFor="pe-desc">What should this panel show?</label>
        <textarea id="pe-desc" rows={3} value={description} onChange={e => {
        setDescription(e.target.value);
        setSpec(null);
        setReadback("");
        setPreview(null);
      }} placeholder={`e.g. "Show me in real time by pharmacy when new appointments for the clinic 'Flu clinic' are made"\nor "Show me a bar chart of near miss incidents by branch this month"`} style={{
        ...S.inp,
        width: "100%",
        boxSizing: "border-box",
        fontFamily: "inherit",
        resize: "vertical",
        margin: "2px 0 10px"
      }} />
        <div style={{
        display: "flex",
        gap: 12,
        alignItems: "flex-end",
        marginBottom: 10,
        flexWrap: "wrap"
      }}>
          <div><label style={S.lab} htmlFor="pe-poll">Refresh every</label><br />
            <select id="pe-poll" value={Number(pollSec)} onChange={e => setPollSec(Number(e.target.value))} style={{
            ...S.inp,
            marginTop: 2
          }}>
              {[...new Set([...POLL_CHOICES, Number(pollSec) || 60])].sort((a, b) => a - b).map(s => <option key={s} value={s}>{pollLabel(s)}</option>)}
            </select></div>
          <button style={S.btn("#1D39C4")} disabled={working || !description.trim()} onClick={interpret}>
            {busy === "interpret" ? "Interpreting…" : "Interpret & test"}</button>
          {spec && <button style={S.btn("#0E8CFF")} disabled={working} onClick={() => test()}>{busy === "test" ? "Testing…" : "↻ Test again"}</button>}
        </div>
        {readback && <div style={{
        background: "#E6F7FF",
        border: "1px solid #91D5FF",
        borderRadius: 6,
        padding: "8px 10px",
        fontSize: 13,
        marginBottom: 10
      }}>
            <b>Understood as:</b> {readback} <span style={{
          color: "#5D5D5D"
        }}>· shown as {visualOf({
            spec
          }).toLowerCase()}</span>
          </div>}
        {(preview || busy === "test") && <div style={{
        ...card,
        background: "#FCFCFC",
        marginBottom: 12
      }}>
            <div style={{
          ...S.lab,
          marginBottom: 6
        }}>Test result — what the panel will show now</div>
            <PanelBody result={busy === "test" ? null : preview} />
          </div>}
        <div style={{
        display: "flex",
        gap: 8,
        justifyContent: "flex-end"
      }}>
          <button style={S.btn("#5D5D5D")} disabled={busy === "save"} onClick={onClose}>Cancel</button>
          <button style={{
          ...S.btn("#0E8CFF", true)
        }} disabled={working || !name.trim() || !description.trim()} onClick={save}>
            {busy === "save" ? "Saving…" : "Save panel"}</button>
        </div>
      </div>
    </div>;
}

/* ---------------- live canvas: one dashboard's panels, polling ---------------- */
// Used by the View popup (editable: move, resize, save layout) and the wall
// view (?wall=, read-only). items: [{id,w,h}].
function DashboardCanvas({
  dashboard,
  byId,
  editable,
  onSaved,
  onPanelChanged
}) {
  const initialItems = useMemo(() => {
    const d = dashboard || {};
    return (Array.isArray(d.items) && d.items.length ? d.items : (d.panelIds || []).map(id => ({
      id,
      w: null,
      h: null
    }))).filter(it => byId[String(it.id)]).map(it => ({
      id: String(it.id),
      w: it.w ?? null,
      h: it.h ?? null
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboard?._id, Object.keys(byId).join(",")]);
  const [canvas, setCanvas] = useState(initialItems);
  const [results, setResults] = useState({});
  const [err, setErr] = useState(null);
  const timers = useRef({});
  const dragIdx = useRef(null);
  const cardRefs = useRef({});
  const loadedSnap = useRef(JSON.stringify(initialItems));
  useEffect(() => {
    setCanvas(initialItems);
    loadedSnap.current = JSON.stringify(initialItems);
  }, [initialItems]);
  const runPanel = useCallback(async panel => {
    try {
      const d = await post("/api/headoffice/dash/run", {
        spec: panel.spec
      });
      setResults(r => ({
        ...r,
        [String(panel._id)]: {
          ...d,
          visual: panel.spec.visual
        }
      }));
    } catch (e) {
      setResults(r => ({
        ...r,
        [String(panel._id)]: {
          error: e.message
        }
      }));
    }
  }, []);
  const pollKey = canvas.map(it => `${it.id}:${byId[it.id]?.pollSec || 60}`).join(",");
  useEffect(() => {
    Object.values(timers.current).forEach(clearInterval);
    timers.current = {};
    for (const it of canvas) {
      const p = byId[it.id];
      if (!p) continue;
      runPanel(p);
      timers.current[it.id] = setInterval(() => runPanel(p), (p.pollSec || 60) * 1000);
    }
    return () => {
      Object.values(timers.current).forEach(clearInterval);
      timers.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollKey]);
  const setSize = (id, w, h) => {
    if (!(w >= 60) || !(h >= 60)) return;
    setCanvas(c => {
      const cur = c.find(it => it.id === id);
      if (!cur) return c;
      const isBaseline = cur.w == null && cur.h == null;
      if (!isBaseline && Math.abs((cur.w || 0) - w) <= 2 && Math.abs((cur.h || 0) - h) <= 2) return c;
      const next = c.map(it => it.id === id ? {
        ...it,
        w,
        h
      } : it);
      if (isBaseline) {
        try {
          loadedSnap.current = JSON.stringify(JSON.parse(loadedSnap.current).map(it => it.id === id ? {
            ...it,
            w,
            h
          } : it));
        } catch (_) {}
      }
      return next;
    });
  };
  useEffect(() => {
    if (!editable || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(entries => {
      for (const en of entries) {
        const id = en.target.getAttribute("data-pid");
        if (id) setSize(id, Math.round(en.target.offsetWidth), Math.round(en.target.offsetHeight));
      }
    });
    for (const it of canvas) {
      const el = cardRefs.current[it.id];
      if (el) ro.observe(el);
    }
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable, canvas.map(it => it.id).join(",")]);
  const dirty = editable && !!dashboard?._id && JSON.stringify(canvas) !== loadedSnap.current;
  const onDrop = idx => {
    const from = dragIdx.current;
    if (from === null || from === idx) return;
    setCanvas(c => {
      const n = [...c];
      const [moved] = n.splice(from, 1);
      n.splice(idx, 0, moved);
      return n;
    });
    dragIdx.current = null;
  };
  const saveLayout = async () => {
    try {
      await post("/api/headoffice/dash/dashboards/save", {
        id: dashboard._id,
        name: dashboard.name,
        items: canvas,
        panelIds: canvas.map(it => it.id)
      });
      loadedSnap.current = JSON.stringify(canvas);
      setErr(null);
      onSaved && onSaved();
    } catch (e) {
      setErr(e.message);
    }
  };
  const changeInterval = async (p, sec) => {
    try {
      await post("/api/headoffice/dash/panels/save", {
        id: p._id,
        name: p.name,
        description: p.description,
        pollSec: sec,
        spec: p.spec,
        readback: p.readback
      });
      onPanelChanged && onPanelChanged();
    } catch (e) {
      setErr(e.message);
    }
  };
  return <div>
      {(dirty || err) && <div style={{
      display: "flex",
      gap: 10,
      alignItems: "center",
      marginBottom: 10
    }}>
          {dirty && <button style={{
        ...S.btn("#b45309", true),
        background: "#FA8C16",
        border: "1px solid #b45309"
      }} onClick={saveLayout} title="Panels have been moved or resized — save the new arrangement into this dashboard.">💾 Save layout</button>}
          {err && <span role="alert" style={{
        color: "#CF1322",
        fontSize: 12.5
      }}>{err}</span>}
        </div>}
      {canvas.length === 0 && <div style={{
      color: "#5D5D5D",
      fontSize: 14,
      padding: 30,
      textAlign: "center",
      border: "2px dashed #F0F0F0",
      borderRadius: 10
    }}>
          {editable ? "This dashboard has no panels yet — add some from the Dashboards list." : "This dashboard has no panels."}
        </div>}
      <div style={{
      display: "flex",
      flexWrap: "wrap",
      gap: 12,
      alignItems: "flex-start"
    }}>
        {canvas.map((it, idx) => {
        const id = it.id;
        const p = byId[id];
        if (!p) return null;
        const res = results[id];
        return <div key={id} data-pid={id} ref={el => {
          cardRefs.current[id] = el;
        }} onDragOver={e => e.preventDefault()} onDrop={() => onDrop(idx)} style={{
          background: "#fff",
          border: "1px solid #F0F0F0",
          borderRadius: 10,
          padding: "10px 12px",
          width: it.w || 430,
          height: it.h || undefined,
          minWidth: 300,
          minHeight: 170,
          maxWidth: "100%",
          resize: editable ? "both" : "none",
          overflow: "auto",
          boxSizing: "border-box"
        }}>
              <div draggable={editable} onDragStart={() => {
            if (editable) dragIdx.current = idx;
          }} style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 6,
            cursor: editable ? "grab" : "default"
          }} title={editable ? "Drag to reorder. Use the bottom-right corner to resize." : undefined}>
                <div style={{
              fontWeight: 800,
              fontSize: 14
            }}>{p.name}</div>
                <div style={{
              display: "flex",
              gap: 6,
              alignItems: "center"
            }}>
                  {editable && <select value={p.pollSec || 60} aria-label="Refresh interval" onChange={e => changeInterval(p, Number(e.target.value))} style={{
                ...S.inp,
                padding: "1px 4px",
                fontSize: 11
              }}>
                      {[...new Set([...POLL_CHOICES, p.pollSec || 60])].sort((a, b) => a - b).map(s => <option key={s} value={s}>{pollLabel(s)}</option>)}
                    </select>}
                  <button style={{
                ...S.btn("#0E8CFF"),
                padding: "1px 7px"
              }} title="Refresh now" aria-label="Refresh now" onClick={() => runPanel(p)}>↻</button>
                </div>
              </div>
              <div style={{
            fontSize: 11.5,
            color: "#0E8CFF",
            fontStyle: "italic",
            margin: "2px 0 1px"
          }}>{p.readback || p.description}</div>
              <div style={{
            fontSize: 10.5,
            color: "#8C8C8C",
            marginBottom: 8
          }}>Refreshes every {pollLabel(p.pollSec || 60)} · last {fmtT(res?.generatedAt)}</div>
              <PanelBody result={res} />
            </div>;
      })}
      </div>
    </div>;
}

/* ================================ main ================================ */
/**
 * @param {string} [wallDashboardId] render this dashboard alone, with no
 *   toolbar and no editing - for its own browser window or a screen on the wall.
 */
export default function DashboardPanels({
  wallDashboardId = null
}) {
  const wall = !!wallDashboardId;
  const [panels, setPanels] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [editor, setEditor] = useState(null); // null | {} (new) | panel
  const [viewing, setViewing] = useState(null); // dashboard shown in the popup
  const [err, setErr] = useState(null);
  const [busyDash, setBusyDash] = useState("");
  const byId = useMemo(() => Object.fromEntries(panels.map(p => [String(p._id), p])), [panels]);
  const loadMeta = useCallback(async () => {
    try {
      const [p, d] = await Promise.all([post("/api/headoffice/dash/panels"), post("/api/headoffice/dash/dashboards")]);
      setPanels(p.panels || []);
      setDashboards(d.dashboards || []);
      setErr(null);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoaded(true);
    }
  }, []);
  useEffect(() => {
    loadMeta();
  }, [loadMeta]);
  const itemsOf = d => (Array.isArray(d.items) && d.items.length ? d.items : (d.panelIds || []).map(id => ({
    id,
    w: null,
    h: null
  }))).map(it => ({
    id: String(it.id),
    w: it.w ?? null,
    h: it.h ?? null
  }));
  const usedIn = pid => dashboards.filter(d => itemsOf(d).some(it => it.id === String(pid))).map(d => d.name);
  const saveDash = async (d, items, name = d.name) => {
    setBusyDash(String(d._id || "new"));
    try {
      await post("/api/headoffice/dash/dashboards/save", {
        id: d._id,
        name,
        items,
        panelIds: items.map(it => it.id)
      });
      await loadMeta();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusyDash("");
    }
  };
  const newDashboard = async () => {
    const name = ((await appPrompt("Name for the new dashboard:", "")) || "").trim();
    if (!name) return;
    if (dashboards.some(d => d.name === name)) {
      (await appAlert(`There is already a dashboard called "${name}".`));
      return;
    }
    await saveDash({}, [], name);
  };
  const deleteDashboard = async d => {
    if (!(await appConfirm(`Delete dashboard "${d.name}"? (Its panels are kept.)`))) return;
    try {
      await post("/api/headoffice/dash/dashboards/delete", {
        id: d._id
      });
      if (viewing?._id === d._id) setViewing(null);
      await loadMeta();
    } catch (e) {
      setErr(e.message);
    }
  };
  const deletePanel = async p => {
    const uses = usedIn(p._id);
    if (!(await appConfirm(`Delete panel "${p.name}"?${uses.length ? `\n\nIt is used in: ${uses.join(", ")} — it will disappear from ${uses.length === 1 ? "that dashboard" : "those dashboards"}.` : ""}`))) return;
    try {
      await post("/api/headoffice/dash/panels/delete", {
        id: p._id
      });
      // Take it out of every dashboard too, so none is left pointing at it.
      for (const d of dashboards) {
        const items = itemsOf(d);
        const next = items.filter(it => it.id !== String(p._id));
        if (next.length !== items.length) await post("/api/headoffice/dash/dashboards/save", {
          id: d._id,
          name: d.name,
          items: next,
          panelIds: next.map(it => it.id)
        });
      }
      await loadMeta();
    } catch (e) {
      setErr(e.message);
    }
  };
  const openWindow = d => window.open(`${window.location.pathname}?wall=${d._id}`, "_blank", "noopener");

  /* ---- wall view: one dashboard, nothing else ---- */
  if (wall) {
    const d = dashboards.find(x => String(x._id) === String(wallDashboardId));
    return <div style={{
      padding: "10px 12px",
      minHeight: "100vh",
      background: "#FAFAFA"
    }}>
        <div style={{
        display: "flex",
        alignItems: "baseline",
        gap: 12,
        marginBottom: 10
      }}>
          <h2 style={{
          margin: 0,
          fontSize: 18,
          color: "#0E8CFF"
        }}>{d?.name || "Dashboard"}</h2>
          <span style={{
          fontSize: 12,
          color: "#8C8C8C"
        }}>live · panels refresh on their own schedule</span>
          {err && <span style={{
          color: "#CF1322",
          fontSize: 12.5
        }}>{err}</span>}
        </div>
        {!loaded ? <div style={{
        color: "#5D5D5D"
      }}>Loading dashboard…</div> : !d ? <div style={{
        color: "#CF1322"
      }}>That dashboard no longer exists.</div> : <DashboardCanvas dashboard={d} byId={byId} editable={false} />}
      </div>;
  }

  /* ---- manager view ---- */
  const colTitle = {
    fontSize: 15,
    fontWeight: 800,
    color: "#262626"
  };
  return <div style={{
    padding: "14px 18px",
    minHeight: "100vh",
    background: "#FAFAFA"
  }}>
      <h2 style={{
      margin: "0 0 8px",
      fontSize: 20,
      color: "#0E8CFF"
    }}>Dashboard panels</h2>
      <div style={{
      ...card,
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
      gap: 16,
      marginBottom: 14,
      fontSize: 13.5,
      lineHeight: 1.55,
      color: "#262626"
    }}>
        <div><b>A panel</b> is the answer to one specific question — for example “near misses by branch this month” or
          “new flu clinic bookings as they happen”. You ask the question in plain English; the panel shows the answer as a
          <b> grid</b>, a <b>graph</b> or a <b>single value</b>, and refreshes itself as often as you choose.</div>
        <div><b>A dashboard</b> is a collection of panels shown together. It can be opened in <b>its own browser
          window</b> — drag it to a second screen and leave it running — and every panel on it <b>updates in real time</b>.
          One panel can appear on as many dashboards as you like.</div>
      </div>
      {err && <div role="alert" style={{
      color: "#CF1322",
      fontSize: 13,
      marginBottom: 10
    }}>{err}</div>}

      <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
      gap: 16,
      alignItems: "start"
    }}>
        {/* ---------------- Panels ---------------- */}
        <section aria-label="Panels">
          <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8
        }}>
            <div style={colTitle}>Panels <span style={{
              color: "#8C8C8C",
              fontWeight: 600
            }}>({panels.length})</span></div>
            <button style={S.btn("#0E8CFF", true)} onClick={() => setEditor({})}>+ New panel</button>
          </div>
          {loaded && panels.length === 0 && <div style={{
          ...card,
          color: "#5D5D5D",
          fontSize: 13
        }}>No panels yet — press <b>+ New panel</b> to ask your first question.</div>}
          {panels.map(p => {
          const uses = usedIn(p._id);
          return <div key={p._id} style={{
            ...card,
            marginBottom: 8
          }}>
                <div style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 8
            }}>
                  <div style={{
                minWidth: 0
              }}>
                    <div style={{
                  fontWeight: 800,
                  fontSize: 14
                }}>{p.name}</div>
                    <div style={{
                  fontSize: 12.5,
                  color: "#434343",
                  marginTop: 2
                }}>{p.description}</div>
                    {p.readback && <div style={{
                  fontSize: 12,
                  color: "#0E8CFF",
                  fontStyle: "italic",
                  marginTop: 2
                }}>Understood as: {p.readback}</div>}
                    <div style={{
                  display: "flex",
                  gap: 6,
                  flexWrap: "wrap",
                  marginTop: 6,
                  fontSize: 11.5
                }}>
                      <span style={{
                    background: "#F0F5FF",
                    color: "#1D39C4",
                    borderRadius: 10,
                    padding: "1px 8px",
                    fontWeight: 700
                  }}>{visualOf(p)}</span>
                      <span style={{
                    background: "#F5F5F5",
                    color: "#434343",
                    borderRadius: 10,
                    padding: "1px 8px"
                  }}>every {pollLabel(p.pollSec || 60)}</span>
                      <span style={{
                    color: "#5D5D5D",
                    alignSelf: "center"
                  }}>{uses.length ? `On: ${uses.join(", ")}` : "Not on any dashboard"}</span>
                    </div>
                  </div>
                  <div style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                flexShrink: 0
              }}>
                    <button style={S.btn("#4338ca")} onClick={() => setEditor(p)}>Edit / test</button>
                    <button style={S.btn("#CF1322")} onClick={() => deletePanel(p)}>Delete</button>
                  </div>
                </div>
              </div>;
        })}
        </section>

        {/* ---------------- Dashboards ---------------- */}
        <section aria-label="Dashboards">
          <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8
        }}>
            <div style={colTitle}>Dashboards <span style={{
              color: "#8C8C8C",
              fontWeight: 600
            }}>({dashboards.length})</span></div>
            <button style={S.btn("#1D39C4", true)} onClick={newDashboard}>+ New dashboard</button>
          </div>
          {loaded && dashboards.length === 0 && <div style={{
          ...card,
          color: "#5D5D5D",
          fontSize: 13
        }}>No dashboards yet — press <b>+ New dashboard</b>, then add panels to it.</div>}
          {dashboards.map(d => {
          const items = itemsOf(d).filter(it => byId[it.id]);
          const free = panels.filter(p => !items.some(it => it.id === String(p._id)));
          const busy = busyDash === String(d._id);
          return <div key={d._id} style={{
            ...card,
            marginBottom: 8,
            opacity: busy ? 0.6 : 1
          }}>
                <div style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap"
            }}>
                  <div style={{
                fontWeight: 800,
                fontSize: 14
              }}>{d.name}</div>
                  <div style={{
                display: "flex",
                gap: 6,
                flexWrap: "wrap"
              }}>
                    <button style={S.btn("#0E8CFF", true)} disabled={!items.length} onClick={() => setViewing(d)} title={items.length ? "Show this dashboard in a popup — move and resize its panels there" : "Add a panel first"}>View</button>
                    <button style={S.btn("#0E8CFF")} disabled={!items.length} onClick={() => openWindow(d)} title="Open this dashboard on its own in a new browser window — no menu — to leave running on another screen">🖥 New window</button>
                    <button style={S.btn("#CF1322")} onClick={() => deleteDashboard(d)}>Delete</button>
                  </div>
                </div>
                <div style={{
              ...S.lab,
              margin: "10px 0 4px"
            }}>Panels on this dashboard</div>
                <div style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              alignItems: "center"
            }}>
                  {items.length === 0 && <span style={{
                fontSize: 12.5,
                color: "#8C8C8C"
              }}>none yet</span>}
                  {items.map(it => <span key={it.id} style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                border: "1px solid #91D5FF",
                background: "#E6F7FF",
                color: "#0958D9",
                borderRadius: 6,
                padding: "2px 4px 2px 9px",
                fontSize: 12.5,
                fontWeight: 700
              }}>
                      {byId[it.id].name}
                      <button aria-label={`Remove ${byId[it.id].name} from ${d.name}`} title="Remove from this dashboard" disabled={busy} onClick={() => saveDash(d, items.filter(x => x.id !== it.id))} style={{
                  border: "none",
                  background: "none",
                  color: "#0958D9",
                  cursor: "pointer",
                  padding: "0 4px",
                  fontSize: 13
                }}>✕</button>
                    </span>)}
                  {free.length > 0 && <select value="" disabled={busy} aria-label={`Add a panel to ${d.name}`} onChange={e => e.target.value && saveDash(d, [...items, {
                id: e.target.value,
                w: null,
                h: null
              }])} style={{
                ...S.inp,
                padding: "3px 6px",
                fontSize: 12.5
              }}>
                      <option value="">+ Add panel…</option>
                      {free.map(p => <option key={p._id} value={String(p._id)}>{p.name}</option>)}
                    </select>}
                </div>
              </div>;
        })}
        </section>
      </div>

      {viewing && (() => {
      const d = dashboards.find(x => String(x._id) === String(viewing._id)) || viewing;
      return <div role="dialog" aria-modal="true" aria-label={d.name} onClick={() => setViewing(null)} style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 9500,
        display: "flex",
        alignItems: "stretch",
        justifyContent: "center",
        padding: 20
      }}>
            <div onClick={e => e.stopPropagation()} style={{
          background: "#FAFAFA",
          borderRadius: 10,
          width: "100%",
          maxWidth: 1600,
          overflow: "auto",
          padding: 16
        }}>
              <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
            marginBottom: 10,
            flexWrap: "wrap"
          }}>
                <div><span style={{
                fontSize: 18,
                fontWeight: 800,
                color: "#0E8CFF"
              }}>{d.name}</span>
                  <span style={{
                fontSize: 12,
                color: "#8C8C8C",
                marginLeft: 10
              }}>live · drag a panel's title to move it, its corner to resize</span></div>
                <div style={{
              display: "flex",
              gap: 8
            }}>
                  <button style={S.btn("#0E8CFF")} onClick={() => openWindow(d)}>🖥 Open in new window</button>
                  <button style={S.btn("#5D5D5D")} onClick={() => setViewing(null)}>Close</button>
                </div>
              </div>
              <DashboardCanvas dashboard={d} byId={byId} editable onSaved={loadMeta} onPanelChanged={loadMeta} />
            </div>
          </div>;
    })()}
      {editor && <PanelEditor initial={editor._id ? editor : null} onClose={() => setEditor(null)} onSaved={async () => {
      setEditor(null);
      await loadMeta();
    }} />}
    </div>;
}
